import { authenticatedFetch } from '../token';
import { config } from '../config';
import { Assembly, CustomComponent } from '../../app/web-api-client';
import { DirectoryFolder } from '../../components/models/DirectoryFolder';

export async function saveAssembly(assembly: Assembly) {
  const resp = await authenticatedFetch(config.APIUrl + '/api/Assembly/save', 'POST', { assembly });
  return await resp.json();
}

export async function duplicateAssembly(assemblyId: string, folderId: any) {
  const resp = await authenticatedFetch(config.APIUrl + '/api/Assembly/duplicate', 'POST', {
    assemblyId,
    folderId
  });
  return await resp.json();
}

export async function getAssembly(id: string) {
  const resp = await authenticatedFetch(config.APIUrl + '/api/Assembly/get?Id=' + id);
  return await resp.json();
}

export async function duplicateGenericAssemblyToReferenceAssembly(id: string) {
  const resp = await authenticatedFetch(
    config.APIUrl + '/api/Assembly/duplicate/generic-to-reference',
    'POST',
    { id }
  );
  return await resp.json();
}

export async function duplicateReferenceAssembly(assemblyId: string, folderId: string) {
  const resp = await authenticatedFetch(
    config.APIUrl + '/api/Assembly/duplicate/reference',
    'POST',
    { assemblyId, folderId }
  );
  return await resp.json();
}

export async function getAllAssemblies() {
  const resp = await authenticatedFetch(config.APIUrl + '/api/Assembly/get/all');
  return await resp.json();
}

export async function getAllAssembliesComplete() {
  const resp = await authenticatedFetch(config.APIUrl + '/api/Assembly/get/all/complete');
  return await resp.json();
}

export async function getAllAssembliesAndReferencesComplete() {
  const resp = await authenticatedFetch(
    config.APIUrl + '/api/Assembly/get/assembliesandreferences/complete'
  );
  return await resp.json();
}

export async function deleteAssembly(id: string) {
  const resp = await authenticatedFetch(config.APIUrl + '/api/Assembly/delete', 'DELETE', { assemblyId: id });
  if (resp.status !== 200 && resp.status !== 204) {
    throw new Error('Error deleting assembly');
  }
  return true;
}

export async function getAssemblyHistory(id: string) {
  const resp = await authenticatedFetch(config.APIUrl + '/api/Assembly/history?Id=' + id);
  return await resp.json();
}

export async function validateAssemblyName(id: string, type: string, name: string) {
  const resp = await authenticatedFetch(
    config.APIUrl + '/api/Assembly/validate/name?Id=' + id + '&Name=' + name + '&Type=' + type
  );
  return await resp.json();
}

export async function getOneAssemblyHistory(id: string, hashPartitionKey: string) {
  const resp = await authenticatedFetch(
    config.APIUrl + '/api/Assembly/get/one/history?Id=' + id + '&PartitionKey=' + hashPartitionKey
  );
  return await resp.json();
}

function urltoFile(url, filename, mimeType) {
  return fetch(url)
    .then(function (res) {
      return res.arrayBuffer();
    })
    .then(function (buf) {
      return new File([buf], filename, { type: mimeType });
    });
}

export async function savePNGAssembly(id: string, image: any) {
  const file = await urltoFile(image, id + '.png', 'image/png');
  return await authenticatedFetch(config.APIUrl + '/api/Image/upload/png', 'POST', file, true);
}

export async function saveSVGAssembly(id: string, image: any) {
  const file = await urltoFile(image, id + '.svg', 'image/svg+xml');
  return await authenticatedFetch(config.APIUrl + '/api/Image/upload/svg', 'POST', file, true);
}

export async function savePNGComponent(id: string, image: any) {
  const file = await urltoFile(image, id + '.png', 'image/png');
  return await authenticatedFetch(
    config.APIUrl + '/api/Image/upload/component/png',
    'POST',
    file,
    true
  );
}

export async function saveSVGComponent(id: string, image: any) {
  const file = await urltoFile(image, id + '.svg', 'image/svg+xml');
  return await authenticatedFetch(
    config.APIUrl + '/api/Image/upload/component/svg',
    'POST',
    file,
    true
  );
}

// Custom Components

export async function saveCustomComponent(component: CustomComponent) {
  const resp = await authenticatedFetch(config.APIUrl + '/api/CustomComponent/save', 'POST', {
    component
  });
  return await resp.json();
}

export async function getCustomComponent(id: string) {
  const resp = await authenticatedFetch(config.APIUrl + '/api/CustomComponent/get?Id=' + id);
  return await resp.json();
}

export async function getAllCustomComponents() {
  const resp = await authenticatedFetch(config.APIUrl + '/api/CustomComponent/get/all');
  return await resp.json();
}

export async function deleteCustomComponent(id: string) {
  await authenticatedFetch(config.APIUrl + '/api/CustomComponent/delete', 'DELETE', {
    componentId: id
  });
  return true;
}

// Directories

export async function getAllDirectories() {
  const resp = await authenticatedFetch(config.APIUrl + '/api/Directory/get/all');
  return await resp.json();
}

export async function saveDirectoryFolder(
  designOwners: string[],
  designEditors: string[],
  directoryFolder: DirectoryFolder
) {
  const resp = await authenticatedFetch(config.APIUrl + '/api/Directory/save', 'POST', {
    designOwners,
    designEditors,
    directoryFolder
  });
  return await resp.json();
}

export async function saveShortcut(folderId: string, shortcutId: string) {
  const resp = await authenticatedFetch(config.APIUrl + '/api/Directory/saveShortcut', 'POST', {
    folderId,
    shortcutId
  });
  return await resp.json();
}

export async function deleteShortcut(folderId: string, shortcutId: string) {
  const resp = await authenticatedFetch(config.APIUrl + '/api/Directory/deleteShortcut', 'DELETE', {
    folderId,
    shortcutId
  });
  return resp.json();
}

export async function deleteDirectoryFolder(id: string) {
  await authenticatedFetch(config.APIUrl + '/api/Directory/delete', 'DELETE', { id });
  return true;
}

// Permissions

export async function getAssemblyPermissions(id: string) {
  const resp = await authenticatedFetch(config.APIUrl + '/api/permission/assembly?id=' + id);
  return await resp.json();
}

export async function moveAssemblyToDirectory(assemblyId: string, folderId: string) {
  const resp = await authenticatedFetch(config.APIUrl + '/api/Directory/move/assembly', 'POST', {
    assemblyId,
    folderId
  });
  return await resp.json();
}

export async function getUserIsAdmin() {
  try {
    const resp = await authenticatedFetch(config.APIUrl + '/api/permission/get/user-role');
    if (resp.ok && resp.status === 200) {
      const text = await resp.text();
      return text ? JSON.parse(text) : null;
    } else {
      console.error("Erreur de réponse de l'API", resp.status);
      return null;
    }
  } catch (error) {
    console.error("Erreur lors de la récupération du rôle de l'utilisateur", error);
    return null;
  }
}
